import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from './ui';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';

interface ClientNote {
  id: string;
  coach_client_id: string;
  appointment_id: string | null;
  content: string;
  created_at: string;
  updated_at: string;
  session_label?: string | null;
  session_date?: string | null;
}

interface SessionSummary {
  id: string;
  scheduled_at: string;
  meeting_name: string;
  status: string;
  summary_available?: boolean;
}

interface SearchResult {
  type: 'note' | 'session_summary';
  id: string;
  content: string;
  appointment_id: string | null;
  session_label: string | null;
  session_date: string | null;
  created_at: string | null;
  score: number;
}

interface ClientNotesTabProps {
  coachClientId: string;
  onGoToSessions: () => void;
  sessionSummaries?: SessionSummary[];
  onViewNotes?: (appt: SessionSummary) => void;
}

export default function ClientNotesTab({ coachClientId, onGoToSessions, sessionSummaries = [], onViewNotes }: ClientNotesTabProps) {
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [newContent, setNewContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());

  const [manualOpen, setManualOpen] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
  });

  useEffect(() => {
    if (!coachClientId || coachClientId === '0') { setLoading(false); return; }
    fetch(`${config.apiBaseUrl}${endpoints.main.clientNotes.list(coachClientId)}`, { headers: headers() })
      .then(r => r.ok ? r.json() : { notes: [] })
      .then(d => setNotes(d.notes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [coachClientId]);

  const handleCreate = async () => {
    if (!newContent.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.clientNotes.create(coachClientId)}`,
        { method: 'POST', headers: headers(), body: JSON.stringify({ content: newContent.trim() }) },
      );
      if (!res.ok) return;
      const data = await res.json();
      setNotes(prev => [data.note, ...prev]);
      setNewContent('');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (note: ClientNote) => {
    if (!editContent.trim()) return;
    const res = await fetch(
      `${config.apiBaseUrl}${endpoints.main.clientNotes.update(coachClientId, note.id)}`,
      { method: 'PATCH', headers: headers(), body: JSON.stringify({ content: editContent.trim() }) },
    );
    if (!res.ok) return;
    const data = await res.json();
    setNotes(prev => prev.map(n => n.id === note.id ? data.note : n));
    setEditingId(null);
  };

  const handleDelete = async (note: ClientNote) => {
    if (!window.confirm('Delete this note?')) return;
    await fetch(
      `${config.apiBaseUrl}${endpoints.main.clientNotes.delete(coachClientId, note.id)}`,
      { method: 'DELETE', headers: headers() },
    );
    setNotes(prev => prev.filter(n => n.id !== note.id));
  };

  const handleSearch = async () => {
    if (!searchQ.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.clientNotes.search(coachClientId, searchQ)}`,
        { headers: headers() },
      );
      if (!res.ok) return;
      const data = await res.json();
      setSearchResults(data.results || []);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => { setSearchQ(''); setSearchResults(null); setExpandedResults(new Set()); };

  const toggleExpand = (key: string) => {
    setExpandedResults(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getSnippet = (content: string, query: string, windowSize = 300): string => {
    const words = query.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    let matchIndex = -1;
    for (const word of words) {
      const idx = content.toLowerCase().indexOf(word);
      if (idx !== -1) { matchIndex = idx; break; }
    }
    if (matchIndex === -1) {
      return content.length > windowSize ? content.slice(0, windowSize) + '...' : content;
    }
    const start = Math.max(0, matchIndex - Math.floor(windowSize / 2));
    const end = Math.min(content.length, start + windowSize);
    return (start > 0 ? '...' : '') + content.slice(start, end) + (end < content.length ? '...' : '');
  };

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-sm font-semibold text-white">Notes</h2>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Search notes and session summaries..."
            className="w-full pl-9 pr-4 py-2 bg-dark-hover border border-dark-border rounded-lg text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:border-primary-500"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={searching || !searchQ.trim()}
          className="px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white rounded-lg text-xs font-medium transition-colors"
        >
          {searching ? '...' : 'Search'}
        </button>
        {searchResults !== null && (
          <button onClick={clearSearch} className="p-2 rounded-lg hover:bg-dark-hover text-gray-400">
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search results */}
      {searchResults !== null && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQ}"</p>
          {searchResults.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-4">No matches found.</p>
          )}
          {searchResults.map(r => {
            const key = `${r.type}-${r.id}`;
            const expanded = expandedResults.has(key);
            const date = r.session_date ? formatDate(r.session_date) : null;
            return (
              <div key={key} className="bg-dark-hover border border-dark-border rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    r.type === 'note' ? 'bg-indigo-900 text-indigo-300' : 'bg-emerald-900 text-emerald-300'
                  }`}>
                    {r.type === 'note' ? 'Note' : 'Session Summary'}
                  </span>
                  {r.session_label && <span className="text-xs text-gray-400">{r.session_label}</span>}
                  {date && <span className="text-xs text-gray-500">{date}</span>}
                  <span className="ml-auto text-xs text-gray-500">{Math.round(r.score * 100)}% match</span>
                </div>
                <p className="text-xs text-gray-200 whitespace-pre-wrap">
                  {expanded ? r.content : getSnippet(r.content, searchQ)}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <button onClick={() => toggleExpand(key)} className="text-xs text-primary-400 hover:text-primary-300 transition-colors">
                    {expanded ? 'Show less' : 'Show full'}
                  </button>
                  {r.type === 'session_summary' && r.appointment_id && (
                    <button onClick={() => { clearSearch(); onGoToSessions(); }} className="text-xs text-gray-400 hover:text-gray-200 transition-colors">
                      Go to session →
                    </button>
                  )}
                  {r.type === 'note' && (
                    <button onClick={clearSearch} className="text-xs text-gray-400 hover:text-gray-200 transition-colors">
                      View in notes →
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {searchResults === null && (
        <div className="space-y-3">

          {/* ── Manual Notes section ── */}
          <div className="border border-dark-border rounded-xl overflow-hidden">
            <button
              onClick={() => setManualOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-dark-hover hover:bg-dark-canvas transition-colors"
            >
              <span className="text-xs font-semibold text-gray-300">Manual Notes</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{notes.length}</span>
                {manualOpen
                  ? <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                  : <ChevronRightIcon className="w-4 h-4 text-gray-400" />}
              </div>
            </button>

            {manualOpen && (
              <div className="p-3 space-y-3 bg-dark-canvas">
                {/* New note input */}
                <div className="bg-dark-hover border border-dark-border rounded-xl p-3">
                  <textarea
                    value={newContent}
                    onChange={e => setNewContent(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleCreate(); }}
                    placeholder="Write a note... (Cmd+Enter to save)"
                    rows={3}
                    className="w-full bg-transparent text-xs text-gray-200 placeholder-gray-500 resize-none focus:outline-none"
                  />
                  <div className="flex justify-end mt-1.5">
                    <button
                      onClick={handleCreate}
                      disabled={saving || !newContent.trim()}
                      className="flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                      {saving ? 'Saving...' : 'Add Note'}
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="flex justify-center py-4"><Spinner /></div>
                ) : notes.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-4">No notes yet.</p>
                ) : (
                  <div className="space-y-2">
                    {notes.map(note => (
                      <div key={note.id} className="bg-dark-hover border border-dark-border rounded-xl p-3">
                        {note.session_label && (
                          <p className="text-xs text-primary-400 mb-1">{note.session_label}</p>
                        )}
                        {editingId === note.id ? (
                          <>
                            <textarea
                              value={editContent}
                              onChange={e => setEditContent(e.target.value)}
                              rows={4}
                              autoFocus
                              className="w-full bg-transparent text-xs text-gray-200 resize-none focus:outline-none"
                            />
                            <div className="flex justify-end gap-2 mt-1.5">
                              <button onClick={() => setEditingId(null)} className="p-1.5 rounded hover:bg-dark-canvas text-gray-400">
                                <XMarkIcon className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => saveEdit(note)} className="p-1.5 rounded hover:bg-dark-canvas text-green-400">
                                <CheckIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-xs text-gray-200 whitespace-pre-wrap">{note.content}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-500">
                                {new Date(note.created_at).toLocaleString(undefined, {
                                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                                })}
                              </span>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => { setEditingId(note.id); setEditContent(note.content); }}
                                  className="p-1.5 rounded hover:bg-dark-canvas text-gray-400"
                                >
                                  <PencilIcon className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleDelete(note)}
                                  className="p-1.5 rounded hover:bg-dark-canvas text-gray-400 hover:text-red-400"
                                >
                                  <TrashIcon className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Session Notes section ── */}
          {sessionSummaries.length > 0 && (
            <div className="border border-dark-border rounded-xl overflow-hidden">
              <button
                onClick={() => setSessionOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-dark-hover hover:bg-dark-canvas transition-colors"
              >
                <span className="text-xs font-semibold text-gray-300">Session Notes</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{sessionSummaries.length}</span>
                  {sessionOpen
                    ? <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                    : <ChevronRightIcon className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {sessionOpen && (
                <div className="divide-y divide-dark-border bg-dark-canvas">
                  {sessionSummaries
                    .slice()
                    .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime())
                    .map(appt => (
                      <div key={appt.id} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <p className="text-xs font-medium text-gray-200">{appt.meeting_name || 'Session'}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{formatDate(appt.scheduled_at)}</p>
                        </div>
                        <button
                          onClick={() => onViewNotes?.(appt)}
                          className="text-xs text-primary-400 hover:text-primary-300 transition-colors shrink-0 ml-4"
                        >
                          View Notes
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}
